import { useEffect, useState } from 'react';

const TRACK_URL = process.env.NODE_ENV === 'development'
  ? '/api/api.php?xAction=trackVisitor'
  : 'https://myblrc.in/api/api.php?xAction=trackVisitor';
const STORAGE_KEY = 'brlc_vid';
const VISITOR_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const TWO_YEARS_IN_SECONDS = 60 * 60 * 24 * 730;

function readCookie(name) {
  const prefix = `${name}=`;
  const parts = document.cookie ? document.cookie.split('; ') : [];
  const match = parts.find((part) => part.startsWith(prefix));
  return match ? decodeURIComponent(match.slice(prefix.length)) : '';
}

function writeCookie(name, value) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${TWO_YEARS_IN_SECONDS}; Path=/; SameSite=Lax${secure}`;
}

function createVisitorId() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

function getOrCreateVisitorId() {
  const stored = localStorage.getItem(STORAGE_KEY) || '';
  const cookie = readCookie(STORAGE_KEY);
  const existing = VISITOR_ID_PATTERN.test(stored) ? stored : cookie;
  if (VISITOR_ID_PATTERN.test(existing)) {
    const id = existing.toLowerCase();
    if (stored !== id) localStorage.setItem(STORAGE_KEY, id);
    if (cookie.toLowerCase() !== id) writeCookie(STORAGE_KEY, id);
    return id;
  }
  const id = createVisitorId().toLowerCase();
  localStorage.setItem(STORAGE_KEY, id);
  writeCookie(STORAGE_KEY, id);
  return id;
}

function VisitorCounter() {
  const [count, setCount] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const recordVisit = async () => {
      try {
        const visitorId = getOrCreateVisitorId();
        const response = await fetch(TRACK_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            visitor_id: visitorId,
            page_path: window.location.pathname || '/',
          }),
        });
        const result = await response.json();
        const nextCount = Number(result?.data?.count);
        if (!cancelled && result?.data?.status === 'success' && Number.isFinite(nextCount)) {
          setCount(nextCount);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Visitor counter:', error);
        }
      }
    };

    recordVisit();
    return () => {
      cancelled = true;
    };
  }, []);

  if (count === null) return null;

  const label = `${count.toLocaleString('en-IN')} ${count === 1 ? 'visitor' : 'visitors'}`;

  return (
    <div
      aria-label={`${label}. Counts first-time visitors only.`}
      style={{
        position: 'fixed',
        left: '16px',
        bottom: '16px',
        zIndex: 20,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 12px',
        borderRadius: '999px',
        background: 'rgba(255, 255, 255, 0.94)',
        border: '1px solid rgba(166, 137, 71, 0.45)',
        color: '#634f23',
        fontSize: '12px',
        fontWeight: 600,
        letterSpacing: '0.01em',
        lineHeight: 1.2,
        boxShadow: '0 2px 8px rgba(99, 79, 35, 0.08)',
        pointerEvents: 'none',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          background: '#a68947',
          flexShrink: 0,
        }}
      />
      {label}
    </div>
  );
}

export default VisitorCounter;
